import React, { useContext } from "react";
import { Box, Grid, List, ListItem } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { useTheme } from "@material-ui/styles";
import Text from "../../../../shared/components/Text";
import MainButton from "../../../../shared/components/MainButton";
import {
  assetIds,
  capitalizeEachWord,
  InformationMessage,
  ThemedAsset,
} from "nemuru-components";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import { THEME_NAMES } from "../../../../../config/themes/config";
import { LOAN_APPLICATION_STATE } from "../../../../../config/config";

export default function ApplicationInfoMessage({
  asset,
  color,
  title,
  subtitle,
  detail,
  instructions,
  button,
  infoMessage,
  handleClick,
}) {
  const {
    title: instructionsTitle,
    titleDynamicDataTexts: instructionsTitleDynamicDataTexts,
    listItems: instructionsListItems,
    style: instructionsStyle,
  } = instructions;

  const { palette } = useTheme();
  const {
    state: { participant, loanApplications, loan },
  } = useContext(ProcessContext);
  const {
    state: { themeName },
  } = useContext(ConfigContext);

  const lenderName =
    loanApplications?.find(
      (la) =>
        la.lender_fields?.loan_application_status?.state ===
        LOAN_APPLICATION_STATE.SELECTED
    )?.lender_name || loan?.loan_application_selected?.lender_name;

  const responseInstructions = () => {
    return (
      <>
        <List
          style={{
            paddingLeft: "8px",
            paddingBottom: "24px",
            width: "100%",
            flex: "0.5",
          }}
        >
          {instructionsListItems.map((listItem) => (
            <ListItem key={listItem.mainTextId} disableGutters>
              <Grid
                container
                display="flex"
                alignItems="flex-start"
                spacing={2}
                color="primary"
              >
                <Grid item xs={1}>
                  {listItem.icon}
                </Grid>
                <Grid item xs={11}>
                  <Typography color="textPrimary" variant="body2">
                    <Text strong tId={listItem.mainTextId} />
                  </Typography>
                  <Typography
                    color="textPrimary"
                    variant={"caption"}
                    className={"mt-1"}
                  >
                    <Text tId={listItem.additionalTextId} />
                  </Typography>
                </Grid>
              </Grid>
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        style={{ padding: "16px 20px 20px 0px" }}
        className="appear-anim"
      >
        {themeName !== THEME_NAMES.caixabank && lenderName ? (
          <Box
            display={"flex"}
            flexDirection={"column"}
            gridGap={8}
            alignItems={"center"}
            justifyContent={"center"}
            mb={4}
          >
            <Typography variant="subtitle2" color="textSecondary">
              <Text tId="FINANCING_PROVIDED_BY" />:
            </Typography>
            <img
              src={`https://cdn.nemuru.com/assets/logos/${lenderName}-logo.svg`}
              alt={lenderName}
              style={{
                height: "42px",
                width: "auto",
              }}
            />
          </Box>
        ) : (
          asset && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent={"center"}
              alignItems={"center"}
              pt={3}
              pb={1}
            >
              {asset}
              <ThemedAsset
                id={assetIds.ICON_HAND}
                width={60}
                height={60}
                style={{ color: palette.text.neutral, marginTop: "-15px" }}
              />
            </Box>
          )
        )}
        {title && (
          <Box>
            <Typography
              style={{ color: palette.text.title }}
              variant={"h6"}
              align={"center"}
              className={"mb-2 transition-all"}
            >
              <strong>
                <Text
                  tId={title}
                  dynamicDataTexts={[
                    capitalizeEachWord(
                      participant?.onboardingDetails.invitation.full_name
                        .first_name
                    ),
                  ]}
                />
              </strong>
            </Typography>
          </Box>
        )}
        {subtitle && (
          <Typography
            variant={"subtitle1"}
            className={"mb-3 transition-all"}
            style={{
              color: color,
              textAlign: "center",
              lineHeight: 1.4,
              padding: "0 16px",
            }}
          >
            <strong>
              <Text tId={subtitle} />
            </strong>
          </Typography>
        )}
        {detail && (
          <Typography
            color={"textSecondary"}
            variant={"body2"}
            className={"mb-3 transition-all mt-2"}
            style={{
              textAlign: "center",
              flex: "0.5",
            }}
          >
            {typeof detail === "string" ? (
              <Text tId={detail} />
            ) : (
              React.isValidElement(detail) && detail
            )}
          </Typography>
        )}

        <Box
          mt={2}
          width={"90%"}
          display="flex"
          justifyContent="flex-start"
          flexDirection="column"
        >
          {instructionsTitle && (
            <Typography
              variant={"body2"}
              color="textPrimary"
              className={"mt-2 px-1 transition-all"}
              style={instructionsStyle}
            >
              <Text
                tId={instructionsTitle}
                dynamicDataTexts={instructionsTitleDynamicDataTexts}
              />
            </Typography>
          )}

          {instructionsListItems && responseInstructions()}
        </Box>
      </Box>

      <Box position="relative" mb={2}>
        {infoMessage && (
          <InformationMessage
            variant={{
              type: "info",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 20 }}>
                  info
                </i>
              ),
            }}
            isScreenXs={true}
            messageContent={{
              body: <Text tId={infoMessage} />,
            }}
          />
        )}
        {button &&
          (typeof button === "string" ? (
            <MainButton color="primary" onClick={() => handleClick()}>
              <Text tId={button} />
            </MainButton>
          ) : (
            React.isValidElement(button) && button
          ))}
      </Box>
    </>
  );
}
